import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/api";
import { useAuth } from "@/contexts/AuthContext";
import {
  Plus, Zap, Trash2, Edit2, ToggleLeft, ToggleRight,
  AlertCircle, Key, Globe, Copy, Download, Upload, Users
} from "lucide-react";

// ── Allocate Modal ─────────────────────────────────────────────────────────────

function AllocateModal({ pipeline, companies, onClose, onSave }) {
  const [selected, setSelected] = useState(new Set(pipeline.allocated_company_ids || []));
  const [saving, setSaving] = useState(false);

  const toggle = (id) => setSelected(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put(`/pipelines/${pipeline.id}/allocate`, { company_ids: [...selected] });
      onSave();
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl border border-slate-200 w-full max-w-sm shadow-xl" onClick={e => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-slate-200 flex items-center gap-2">
          <Users size={14} className="text-[#006080]" />
          <div>
            <h3 className="font-bold text-slate-900 text-sm">Allocate "{pipeline.name}"</h3>
            <p className="text-xs text-slate-400">Select which companies can use this global pipeline</p>
          </div>
        </div>
        <div className="p-4 max-h-64 overflow-y-auto space-y-1">
          {companies.map(co => (
            <label key={co.id} className="flex items-center gap-2.5 p-2 rounded-md hover:bg-slate-50 cursor-pointer">
              <input type="checkbox" checked={selected.has(co.id)} onChange={() => toggle(co.id)}
                className="w-4 h-4 rounded border-slate-300 text-[#006080] focus:ring-[#006080]" />
              <span className="text-sm text-slate-700">{co.name}</span>
            </label>
          ))}
        </div>
        <div className="px-4 py-3 border-t border-slate-200 flex gap-2">
          <button onClick={onClose} className="flex-1 h-8 border border-slate-200 text-sm text-slate-600 rounded-md hover:bg-slate-50">Cancel</button>
          <button onClick={handleSave} disabled={saving} className="flex-1 h-8 bg-[#006080] text-white text-sm font-medium rounded-md disabled:opacity-50">
            {saving ? "Saving…" : "Save Allocation"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Import Modal ───────────────────────────────────────────────────────────────

function ImportModal({ onClose, onSave }) {
  const [json, setJson] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const fileRef = useRef();

  const handleFile = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = ev => setJson(ev.target.result);
    reader.readAsText(f);
  };

  const handleImport = async () => {
    setError("");
    let parsed;
    try { parsed = JSON.parse(json); } catch { setError("Invalid JSON"); return; }
    setSaving(true);
    try {
      await api.post("/pipelines/import", parsed);
      onSave();
    } catch (err) {
      setError(err.response?.data?.detail || "Import failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl border border-slate-200 w-full max-w-md shadow-xl" onClick={e => e.stopPropagation()}>
        <div className="px-5 py-4 border-b border-slate-200 flex items-center gap-2">
          <Upload size={14} className="text-[#006080]" />
          <h3 className="font-bold text-slate-900 text-sm">Import Pipeline</h3>
        </div>
        <div className="p-5 space-y-3">
          {error && <div className="text-xs text-red-600 bg-red-50 border border-red-200 rounded px-3 py-2">{error}</div>}
          <div className="flex items-center gap-2">
            <button onClick={() => fileRef.current?.click()} className="h-8 px-3 text-xs font-medium border border-slate-200 rounded-md hover:bg-slate-50 text-slate-600">
              Choose JSON file
            </button>
            <span className="text-xs text-slate-400">or paste JSON below</span>
            <input ref={fileRef} type="file" accept=".json" onChange={handleFile} className="hidden" />
          </div>
          <textarea value={json} onChange={e => setJson(e.target.value)} rows={7}
            placeholder='{"name": "My Pipeline", "schema_ids": [...], ...}'
            className="w-full px-3 py-2 text-xs font-mono border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-[#006080] resize-none" />
        </div>
        <div className="px-5 pb-4 flex gap-2">
          <button onClick={onClose} className="flex-1 h-8 border border-slate-200 text-sm text-slate-600 rounded-md hover:bg-slate-50">Cancel</button>
          <button onClick={handleImport} disabled={saving || !json.trim()} className="flex-1 h-8 bg-[#006080] text-white text-sm font-medium rounded-md disabled:opacity-50">
            {saving ? "Importing…" : "Import Pipeline"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────────

export default function PipelineList() {
  const [pipelines, setPipelines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [companies, setCompanies] = useState([]);
  const [allocateTarget, setAllocateTarget] = useState(null);
  const [showImport, setShowImport] = useState(false);
  const { user } = useAuth();
  const isSuperAdmin = user?.role === "superadmin";

  const fetchPipelines = () => {
    setLoading(true);
    const calls = [api.get("/pipelines")];
    if (isSuperAdmin) calls.push(api.get("/admin/companies"));
    Promise.all(calls)
      .then(([r, c]) => {
        setPipelines(r.data);
        if (c) setCompanies(c.data.filter(co => co.is_active));
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { fetchPipelines(); }, []);

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this pipeline and all its API keys?")) return;
    setError("");
    try {
      await api.delete(`/pipelines/${id}`);
      fetchPipelines();
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to delete pipeline");
      setTimeout(() => setError(""), 4000);
    }
  };

  const handleToggle = async (pl) => {
    try {
      await api.put(`/pipelines/${pl.id}`, { is_active: !pl.is_active });
      setPipelines(prev => prev.map(p => p.id === pl.id ? { ...p, is_active: !pl.is_active } : p));
    } catch {}
  };

  const handleDuplicate = async (pl) => {
    try {
      await api.post(`/pipelines/${pl.id}/duplicate`);
      fetchPipelines();
    } catch {}
  };

  const handleExport = async (pl) => {
    try {
      const r = await api.get(`/pipelines/${pl.id}/export`);
      const blob = new Blob([JSON.stringify(r.data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${pl.name.replace(/\s+/g, "_")}.pipeline.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {}
  };

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-slate-900">Pipelines</h2>
          <p className="text-sm text-slate-500 mt-0.5">Multi-schema extraction endpoints with dedicated API keys.</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowImport(true)}
            data-testid="import-pipeline-button"
            className="flex items-center gap-1.5 h-9 px-3 text-sm font-medium border border-slate-200 bg-white text-slate-600 rounded-md hover:bg-slate-50 transition-colors"
          >
            <Upload size={13} /> Import
          </button>
          <Link
            to="/pipelines/new"
            data-testid="create-pipeline-button"
            className="flex items-center gap-2 h-9 px-4 bg-[#006080] hover:bg-[#004B66] text-white text-sm font-medium rounded-md transition-colors"
          >
            <Plus size={14} /> New Pipeline
          </Link>
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
          <AlertCircle size={14} className="flex-shrink-0" /> {error}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-16"><div className="w-6 h-6 border-2 border-[#006080] border-t-transparent rounded-full animate-spin" /></div>
      ) : pipelines.length === 0 ? (
        <div className="text-center py-16 bg-white border border-slate-200 rounded-lg">
          <Zap size={32} className="text-slate-300 mx-auto mb-3" />
          <p className="text-sm text-slate-500 mb-1">No pipelines yet</p>
          <p className="text-xs text-slate-400 mb-4">Pipelines let you run multiple schemas against one document in a single API call.</p>
          <Link to="/pipelines/new" className="h-9 px-4 bg-[#006080] text-white text-sm font-medium rounded-md hover:bg-[#004B66] inline-flex items-center gap-2">
            <Plus size={14} /> Create your first pipeline
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {pipelines.map(pl => {
            const isGlobal = pl.scope === "global";
            return (
              <div key={pl.id} data-testid={`pipeline-card-${pl.id}`}
                className={`bg-white border rounded-lg p-5 hover:-translate-y-0.5 hover:shadow-sm transition-all ${pl.is_active ? "border-slate-200" : "border-slate-100 opacity-60"}`}>
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className={`w-8 h-8 rounded-md flex-shrink-0 flex items-center justify-center ${isGlobal ? "bg-purple-50 text-purple-600" : "bg-[#E5F0F2] text-[#006080]"}`}>
                      {isGlobal ? <Globe size={14} /> : <Zap size={14} />}
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-slate-900">{pl.name}</h3>
                      <div className="flex items-center gap-1">
                        <span className={`text-[10px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded-full ${pl.is_active ? "bg-emerald-50 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>
                          {pl.is_active ? "Active" : "Inactive"}
                        </span>
                        {isGlobal && (
                          <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-purple-50 text-purple-600">Global</span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {pl.description && <p className="text-xs text-slate-500 mb-3 line-clamp-2">{pl.description}</p>}

                <div className="flex items-center gap-3 text-xs text-slate-400 mb-4">
                  <span>{pl.schema_ids?.length || 0} schema{pl.schema_ids?.length !== 1 ? "s" : ""}</span>
                  <span>·</span>
                  <span>min {pl.min_fields_matched} field{pl.min_fields_matched !== 1 ? "s" : ""}</span>
                  {pl.webhook_id && <><span>·</span><span className="text-[#006080]">webhook</span></>}
                  {isGlobal && pl.allocated_company_ids?.length > 0 && (
                    <><span>·</span><span className="text-purple-500">{pl.allocated_company_ids.length} co.</span></>
                  )}
                </div>

                <div className="flex items-center gap-1.5 flex-wrap">
                  <Link to={`/pipelines/${pl.id}`} data-testid={`edit-pipeline-${pl.id}`}
                    className="flex items-center gap-1 h-7 px-2 text-xs font-medium border border-slate-200 rounded-md bg-white hover:bg-slate-50 transition-colors">
                    <Edit2 size={10} /> Edit
                  </Link>
                  <Link to={`/pipelines/${pl.id}?tab=keys`} data-testid={`keys-pipeline-${pl.id}`}
                    className="flex items-center gap-1 h-7 px-2 text-xs font-medium border border-slate-200 rounded-md bg-white hover:bg-slate-50 transition-colors">
                    <Key size={10} /> Keys
                  </Link>
                  <button onClick={() => handleToggle(pl)} data-testid={`toggle-pipeline-${pl.id}`}
                    className="flex items-center gap-1 h-7 px-2 text-xs font-medium border border-slate-200 rounded-md bg-white hover:bg-slate-50 transition-colors">
                    {pl.is_active ? <ToggleRight size={12} className="text-emerald-600" /> : <ToggleLeft size={12} className="text-slate-400" />}
                    {pl.is_active ? "Disable" : "Enable"}
                  </button>
                  <button onClick={() => handleDuplicate(pl)} data-testid={`duplicate-pipeline-${pl.id}`} title="Duplicate"
                    className="flex items-center gap-1 h-7 px-2 text-xs font-medium border border-slate-200 rounded-md bg-white hover:bg-slate-50 transition-colors text-slate-500">
                    <Copy size={10} />
                  </button>
                  <button onClick={() => handleExport(pl)} data-testid={`export-pipeline-${pl.id}`} title="Export JSON"
                    className="flex items-center gap-1 h-7 px-2 text-xs font-medium border border-slate-200 rounded-md bg-white hover:bg-slate-50 transition-colors text-slate-500">
                    <Download size={10} />
                  </button>
                  {isSuperAdmin && isGlobal && (
                    <button onClick={() => setAllocateTarget(pl)} data-testid={`allocate-pipeline-${pl.id}`} title="Allocate to Companies"
                      className="flex items-center gap-1 h-7 px-2 text-xs font-medium border border-purple-200 rounded-md bg-purple-50 hover:bg-purple-100 text-purple-600 transition-colors">
                      <Users size={10} /> Allocate
                    </button>
                  )}
                  <button onClick={() => handleDelete(pl.id)} data-testid={`delete-pipeline-${pl.id}`}
                    className="flex items-center h-7 px-2 text-xs font-medium border border-slate-200 rounded-md bg-white hover:bg-red-50 hover:border-red-200 hover:text-red-600 transition-colors text-slate-400">
                    <Trash2 size={10} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {allocateTarget && (
        <AllocateModal
          pipeline={allocateTarget}
          companies={companies}
          onClose={() => setAllocateTarget(null)}
          onSave={() => { setAllocateTarget(null); fetchPipelines(); }}
        />
      )}
      {showImport && (
        <ImportModal
          onClose={() => setShowImport(false)}
          onSave={() => { setShowImport(false); fetchPipelines(); }}
        />
      )}
    </div>
  );
}
